import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { useTranslation } from 'react-i18next';
import { AthleteAvatar } from '../../../../components/icons/athlete-avatar';
import { ConfirmationModal } from '../../../../components/modals/confirmation-modal';

export type AthleteChangeConfirmationData = {
  show: boolean;
  added: AthletePreview[];
  removed: AthletePreview[];
};

export function ChangeAthleteConfirmationModal({
  id,
  name,
  confirmationData,
  onConfirm,
  onCancel,
}: {
  id: string;
  name: string;
  confirmationData: AthleteChangeConfirmationData;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      id={id}
      title={t('confirmTrackChanges')}
      open={confirmationData.show}
      confirmText={t('saveChanges')}
      cancelText={t('cancel')}
      confirmButtonVariant="containedDestructive"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className="text-sm max-w-[400px]">
        <p>{t('changesOccurred', { name })}</p>
        {confirmationData.added.length > 0 && (
          <AthleteChangedList
            title={t('added')}
            athleteList={confirmationData.added}
          />
        )}
        {confirmationData.removed.length > 0 && (
          <AthleteChangedList
            title={t('removed')}
            athleteList={confirmationData.removed}
            className={confirmationData.added.length ? 'mt-2' : ''}
          />
        )}
        {confirmationData.added.length > 0 && (
          <p>
            Current and future blocks in {name} have to be republished to assign
            any planned workouts to the added athletes.
          </p>
        )}
        {confirmationData.removed.length > 0 && (
          <p>
            Future published workouts in {name} will be canceled for removed
            athletes.
          </p>
        )}
      </div>
    </ConfirmationModal>
  );
}

function AthleteChangedList({
  title,
  athleteList,
  className,
}: {
  title: string;
  athleteList: AthletePreview[];
  className?: string;
}) {
  return (
    <div className={className}>
      <Typography className="text-sm font-bold">{title}</Typography>
      <div className="p-2 border-gray-200 rounded border overflow-y-auto max-h-[200px]">
        <List dense={true} sx={{ padding: 0 }}>
          {athleteList.map((athlete) => {
            return (
              <ListItem
                key={athlete.id}
                sx={{
                  pointerEvents: 'auto',
                  backgroundColor: 'white',
                }}
              >
                <ListItemAvatar sx={{ minWidth: 0, paddingRight: '8px' }}>
                  <AthleteAvatar
                    athlete={athlete}
                    sx={{ width: 24, height: 24, fontSize: 12 }}
                    imgSrc={athlete.imageUrl ?? ''}
                  />
                </ListItemAvatar>
                <ListItemText primary={athleteDisplayName(athlete)} />
              </ListItem>
            );
          })}
        </List>
      </div>
    </div>
  );
}
